import type { WeakValidationMap } from 'react';
import type React from 'react';
import { useCallback, useEffect } from 'react';
import { describe, PropTypes } from '../props-helpers/index.js';

const useFocusTrapProps: { [index: string]: string } = {
  firstElementRef: 'object',
  lastElementRef: 'object',
  onKeyDown: 'function',
};

const getVariableType = (arg: unknown): string => {
  if (Array.isArray(arg)) return 'array';
  if (arg === null) return 'null';
  if (arg === undefined) return 'undefined';
  return typeof arg;
};

const throwTypeError = (validPropKey: string, invalidProp: unknown): void => {
  throw new Error(
    `Warning: You are trying to pass a not valid "${validPropKey}" property to use a useFocusTrap hook, please provide a valid type.

  Received: ${getVariableType(invalidProp)}
  Expected: ${useFocusTrapProps[validPropKey]}
  `,
  );
};

const throwRequiredError = (validPropKey: string): void => {
  throw new Error(
    `Warning: Please provide a "${validPropKey}" property to use a useFocusTrap hook, this property is required.`,
  );
};

const validateProps = (componentProps: Record<string, unknown>): void => {
  if (componentProps.firstElementRef === undefined) {
    throwRequiredError('firstElementRef');
  }
  if (componentProps.lastElementRef === undefined) {
    throwRequiredError('lastElementRef');
  }
  if (componentProps.firstElementRef && typeof componentProps.firstElementRef !== 'object') {
    throwTypeError('firstElementRef', componentProps.firstElementRef);
  }
  if (componentProps.lastElementRef && typeof componentProps.lastElementRef !== 'object') {
    throwTypeError('lastElementRef', componentProps.lastElementRef);
  }
  if (componentProps.onKeyDown && typeof componentProps.onKeyDown !== 'function') {
    throwTypeError('onKeyDown', componentProps.onKeyDown);
  }
};

const propTypes = {
  firstElementRef: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.node]).description(
    'Reference to the first element in the focus trap.',
  ).isRequired,
  lastElementRef: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.node]).description(
    'Reference to the second element in the focus trap.',
  ).isRequired,
  onKeyDown: PropTypes.func.description('Optional onKeyDown callback.'),
} as WeakValidationMap<unknown>;

type UseFocusTrapFunctionT = <T extends HTMLElement = HTMLElement, K extends HTMLElement = HTMLElement>({
  firstElementRef,
  lastElementRef,
  onKeyDown,
}: {
  firstElementRef: React.MutableRefObject<T | null>;
  lastElementRef: React.MutableRefObject<K | null>;
  onKeyDown?: React.KeyboardEventHandler;
}) => React.KeyboardEventHandler;

interface UseFocusTrapT extends UseFocusTrapFunctionT {
  displayName: 'useFocusTrap';
}

const useFocusTrap: UseFocusTrapT = (props) => {
  // We need to investigate how to validate references with useValidateTypescriptPropTypes.
  useEffect(() => validateProps(props), [props]);

  const { firstElementRef, lastElementRef, onKeyDown } = props;

  return useCallback(
    (e) => {
      if (onKeyDown) onKeyDown(e);
      // we need to check all the condition before preventing default
      // because we want last-element/first-element tab/shift-tab to keep working

      if (e.key === 'Tab' && !e.shiftKey && e.target === lastElementRef.current && firstElementRef.current) {
        e.preventDefault();
        e.stopPropagation();
        firstElementRef.current.focus();
      }
      if (e.key === 'Tab' && e.shiftKey && e.target === firstElementRef.current && lastElementRef.current) {
        e.preventDefault();
        e.stopPropagation();
        lastElementRef.current.focus();
      }
    },
    [firstElementRef, lastElementRef, onKeyDown],
  );
};

useFocusTrap.displayName = 'useFocusTrap';
const UseFocusTrapWithSchema = describe(useFocusTrap);

UseFocusTrapWithSchema.propTypes = propTypes;

export { useFocusTrap, UseFocusTrapWithSchema };
